<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import BotLauncher from './BotLauncher.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton, BotLauncher },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'toggleTemplates',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      // Íconos SIEMPRE visibles para todos (Victor 2026-05-24): el row de
      // acciones no depende del bloqueo del editor. El bloqueo real (canal
      // caído / ventana 24h) sigue impidiendo ENVIAR vía isEditorDisabled en
      // el editor y el botón Enviar, no escondiendo los íconos.
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      return false;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    // Abre el selector de archivos de forma confiable (Victor 2026-06-04). El
    // clip es un <button> dentro del <label> de vue-upload-component, y por spec
    // HTML un <button> dentro de un <label for> NO dispara el input de archivo
    // (el botón "consume" el clic) → en PC no abría nada. Disparamos el input a
    // mano: .stop/.prevent evita el doble disparo y buscamos el input DENTRO de
    // este FileUpload (no por id global) para no chocar si hay otra instancia.
    openFilePicker() {
      const root = this.$refs.uploadRef?.$el;
      const input =
        root?.querySelector('input[type="file"]') ||
        document.getElementById('conversationAttachment');
      if (input) input.click();
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
  },
};
</script>

<template>
  <div
    class="reply-bottom-panel-r4 flex items-center gap-1 px-2 py-1.5"
    :class="[wrapClass, $slots.editor ? 'is-inline-pill' : 'is-block-row']"
  >
    <div class="left-wrap">
      <NextButton
        v-if="!isOnPrivateNote"
        v-tooltip.top-end="'Plantillas de mensajes'"
        icon="i-lucide-sparkles"
        amber
        faded
        sm
        @click="$emit('toggleTemplates')"
      />
      <span v-if="!isOnPrivateNote" class="r4-divider" aria-hidden="true" />
      <!-- Emoji a color + rebote y confeti (Victor 2026-06-05 · P1 variante D).
           Botón propio (no NextButton) para poder pintar la carita a color. -->
      <button
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        type="button"
        class="r4-emoji"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        @click="toggleEmojiPicker"
      >
        <span class="r4-confetti c1" aria-hidden="true" />
        <span class="r4-confetti c2" aria-hidden="true" />
        <span class="r4-confetti c3" aria-hidden="true" />
        <span class="r4-confetti c4" aria-hidden="true" />
        <svg class="r4-emoji-face" viewBox="0 0 24 24" aria-hidden="true">
          <defs>
            <radialGradient id="r4FaceGrad" cx="50%" cy="34%" r="75%">
              <stop offset="0%" stop-color="#ffe680" />
              <stop offset="55%" stop-color="#ffc23c" />
              <stop offset="100%" stop-color="#f59e0b" />
            </radialGradient>
          </defs>
          <circle cx="12" cy="12" r="10" fill="url(#r4FaceGrad)" stroke="#e08e0a" stroke-width="1" />
          <circle cx="8.6" cy="10.3" r="1.25" fill="#5a3a00" />
          <circle cx="15.4" cy="10.3" r="1.25" fill="#5a3a00" />
          <path d="M8 14.2c1 1.4 2.5 2.1 4 2.1s3-.7 4-2.1" fill="none" stroke="#5a3a00" stroke-width="1.5" stroke-linecap="round" />
        </svg>
      </button>
      <FileUpload
        v-if="showAttachButton"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
          @click.stop.prevent="openFilePicker"
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <BotLauncher
        v-if="!isOnPrivateNote && conversationId"
        :conversation-id="conversationId"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="showQuotedReplyToggle"
        v-tooltip.top-end="quotedReplyToggleTooltip"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />
      <NextButton
        v-if="enableWhatsAppTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <NextButton
        v-if="enableContentTemplates"
        v-tooltip.top-end="'Content Templates'"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectContentTemplate')"
      />
      <VideoCallButton
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div v-if="$slots.editor" class="r4-editor-slot">
      <slot name="editor" />
    </div>
    <div class="right-wrap">
      <button
        v-tooltip.top-end="sendButtonText"
        type="submit"
        :disabled="isSendDisabled"
        class="r4-send"
        :class="{ 'r4-send--note': isNote, 'r4-send--disabled': isSendDisabled }"
        :aria-label="sendButtonText"
        @click="onSend"
      >
        <fluent-icon icon="send" size="14" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-0.5 flex-shrink-0;
}

.right-wrap {
  @apply flex items-center flex-shrink-0;
}

/* Block-row layout: cuando no hay editor en slot (audio recording, copilot) */
.reply-bottom-panel-r4.is-block-row {
  @apply justify-between;
}

/* Inline-pill layout: editor slot crece para llenar el espacio entre iconos y send */
.r4-editor-slot {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 6px;
}
.r4-editor-slot :deep(> *) {
  flex: 1;
  min-width: 0;
}
.r4-editor-slot :deep(.ProseMirror) {
  min-height: 24px;
  max-height: 120px;
  overflow-y: auto;
  padding: 4px 0;
  font-size: 13.5px;
}
.r4-editor-slot :deep(.ProseMirror p) {
  margin: 0;
  line-height: 1.45;
}

/* === R4 · Iconos mini circulares 28x28 (WhatsApp/iMessage style) === */
.reply-bottom-panel-r4 :deep(.left-wrap > button),
.reply-bottom-panel-r4 :deep(.file-uploads button) {
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
  padding: 0 !important;
  color: rgba(255, 255, 255, 0.55);
  background: transparent;
  outline: none !important;
  transition: background 0.15s ease, color 0.15s ease;
}
/* Hover DORADO + leve elevación (Victor 2026-06-05 · P1 elegante). El emoji a
   color queda excluido — tiene su propia animación de rebote/confeti. */
.reply-bottom-panel-r4 :deep(.left-wrap > button:not(.r4-emoji):hover:not(:disabled)),
.reply-bottom-panel-r4 :deep(.file-uploads button:hover:not(:disabled)) {
  background: rgba(201, 163, 90, 0.12) !important;
  color: #c9a35a !important;
  transform: translateY(-1px);
}
.reply-bottom-panel-r4 :deep(.left-wrap > button .iconify),
.reply-bottom-panel-r4 :deep(.file-uploads button .iconify) {
  width: 14px;
  height: 14px;
}

/* === Divisor sutil entre el botón de plantillas y las herramientas === */
.r4-divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 4px;
  flex-shrink: 0;
}

/* === Emoji a color · rebote + confeti al pasar el mouse (P1 variante D) === */
.r4-emoji {
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  outline: none;
}
.r4-emoji-face {
  width: 19px;
  height: 19px;
  display: block;
  transition: transform 0.15s ease;
}
.r4-emoji:hover .r4-emoji-face {
  animation: r4-emoji-bounce 0.5s ease;
}
@keyframes r4-emoji-bounce {
  0%,
  100% {
    transform: translateY(0) rotate(0);
  }
  30% {
    transform: translateY(-3px) rotate(-6deg);
  }
  60% {
    transform: translateY(0) rotate(4deg);
  }
}
.r4-confetti {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.r4-emoji:hover .c1 {
  opacity: 1;
  transform: translate(-11px, -11px);
}
.r4-emoji:hover .c2 {
  opacity: 1;
  transform: translate(7px, -11px);
}
.r4-emoji:hover .c3 {
  opacity: 1;
  transform: translate(9px, 7px);
}
.r4-emoji:hover .c4 {
  opacity: 1;
  transform: translate(-9px, 8px);
}
.c1 {
  background: #ff6b6b;
}
.c2 {
  background: #4d96ff;
}
.c3 {
  background: #6bcb77;
}
.c4 {
  background: #b76bff;
}

/* === R4 · Send button circular dorado (M1.B) === */
.r4-send {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border: 0;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.30);
  transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(.r4-send--disabled) {
    transform: scale(1.05);
    box-shadow: 0 3px 12px rgba(251, 191, 36, 0.45);
  }
  &:active:not(.r4-send--disabled) {
    transform: scale(0.95);
  }
}
.r4-send--note {
  background: linear-gradient(135deg, #fbbf24, #d97706);
}
.r4-send--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }
}
</style>
